import React, { useState } from 'react';
import { ArrowUpCircle } from 'lucide-react';
import type {
  ActivityEntry,
  ChangeDetail,
  ChangesContext
} from 'shared/src/api/activity-feed-api-types';
import { ActivityCardShell } from './ActivityCardShell';
import { ActivityExpandToggle } from './ActivityExpandToggle';

type ChangesetCardProps = {
  entry: ActivityEntry;
  showCampaignName: boolean;
};

export function ChangesetCard({ entry, showCampaignName }: ChangesetCardProps) {
  const [expanded, setExpanded] = useState(false);
  const changes = entry.changes ?? [];
  const visibleChanges = expanded ? changes : changes.slice(0, 3);
  const hiddenCount = changes.length - 3;
  const isPromotion = entry.contentTitle === 'Promoted to Execution';

  return (
    <ActivityCardShell entry={entry} showCampaignName={showCampaignName}>
      {isPromotion ? (
        <div className="flex items-center gap-1.5 text-sm text-blue-700">
          <ArrowUpCircle className="size-3.5" />
          <span>Promoted to Execution</span>
        </div>
      ) : visibleChanges.length > 0 ? (
        <div className="space-y-1">
          {visibleChanges.map((change, idx) => (
            <ChangeRow key={idx} change={change} changesContext={entry.changesContext} />
          ))}
          {hiddenCount > 0 ? (
            <ActivityExpandToggle
              expanded={expanded}
              onToggle={() => setExpanded(prev => !prev)}
              collapsedLabel={`${hiddenCount} more ${hiddenCount === 1 ? 'field' : 'fields'}`}
              expandedLabel="Show less"
            />
          ) : null}
        </div>
      ) : null}
    </ActivityCardShell>
  );
}

/* ---------------------------------------------------------------------------
 * Private helpers
 * --------------------------------------------------------------------------- */

function ChangeRow({
  change,
  changesContext
}: {
  change: ChangeDetail;
  changesContext: ChangesContext | null | undefined;
}) {
  if (change.kind === 'bravo') {
    const oldDisplay = resolveContextValue(change.fieldPath, change.oldValue, changesContext);
    const newDisplay = resolveContextValue(change.fieldPath, change.newValue, changesContext);
    return (
      <div className="text-muted-foreground flex items-baseline gap-1 text-sm">
        <span className="font-medium text-gray-700">{formatFieldLabel(change.fieldPath)}:</span>
        <span>{formatChangeValue(oldDisplay)}</span>
        <span>&rarr;</span>
        <span>{formatChangeValue(newDisplay)}</span>
      </div>
    );
  }

  return (
    <div className="text-muted-foreground text-sm">
      <span className="font-medium text-gray-700">{change.field}:</span> {change.changeCount}{' '}
      {change.changeCount === 1 ? 'change' : 'changes'}
    </div>
  );
}

const FIELD_URN_PATTERNS: Record<string, (id: string) => string> = {
  channel: id => `urn:bravo/channel:${id}`,
  tactic: id => `urn:bravo/tactic:${id}`,
  unit_price_type: id => `urn:bravo/unit_price_type:${id}`,
  media_traders: id => `urn:bravo/user:${id}`,
  media_platforms: id => `urn:bravo/media_platform:${id}`,
  owner: id => `urn:zoho/user:${id}`,
  lead_account_owner: id => `urn:zoho/user:${id}`,
  sales_rep: id => `urn:zoho/user:${id}`,
  account: id => `urn:bravo/account:${id}`,
  agency: id => `urn:bravo/account:${id}`,
  campaign_source: id => `urn:bravo/campaign_source:${id}`
};

function resolveContextValue(
  fieldPath: string,
  rawValue: string | null,
  context: ChangesContext | null | undefined
): string | null {
  if (rawValue == null || !context) return rawValue;

  const urnFn = FIELD_URN_PATTERNS[fieldPath];
  if (!urnFn) return rawValue;

  // Array fields: parse JSON array, resolve each element
  if (fieldPath === 'media_traders' || fieldPath === 'media_platforms') {
    if (rawValue.startsWith('[')) {
      try {
        const ids = JSON.parse(rawValue) as unknown[];
        if (Array.isArray(ids) && ids.length > 0) {
          const resolved = ids.map(id => {
            const urn = urnFn(String(id));
            return context[urn]?.name ?? String(id);
          });
          return JSON.stringify(resolved);
        }
      } catch {
        // Not valid JSON — fall through to raw value
      }
    }
    return rawValue;
  }

  // Scalar fields: single URN lookup
  const urn = urnFn(rawValue);
  return context[urn]?.name ?? rawValue;
}

function formatFieldLabel(fieldPath: string): string {
  return fieldPath
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatChangeValue(value: string | null): string {
  if (value == null) return '(empty)';

  // Handle JSON-stringified arrays and objects from the backend
  if (value.startsWith('[') || value.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return '(empty)';
        // For arrays of primitives (names), join with commas
        // For arrays of objects (pacing_details), summarize
        return parsed
          .map(item => (typeof item === 'object' && item !== null ? summarizeObject(item) : item))
          .join(', ');
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return summarizeObject(parsed);
      }
    } catch {
      // Not valid JSON — fall through to raw display
    }
  }

  return value || '(empty)';
}

function summarizeObject(obj: object): string {
  const entries = Object.entries(obj as Record<string, unknown>);
  return entries
    .map(([key, val]) => {
      if (typeof val === 'object' && val !== null) return `${formatFieldLabel(key)}: ...`;
      return `${formatFieldLabel(key)}: ${val}`;
    })
    .join(', ');
}
